import { defineStore } from "pinia";
import { ref, computed } from "vue";
import * as Sentry from "@sentry/vue";

const language = window.navigator.language;
const SESSION_TIMEOUT = 4 * 60 * 1000;

interface APIHeaders {
  "Content-Type": string;
  "X-USER-LOGIN": string | null;
  "X-USER-MONITORING-PROGRAM-CODE": string | null;
  "X-USER-TOKEN": string | null;
  "Accept-Language": string;
}

export const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string | null>(localStorage.accessToken ?? null);
  const tenantCode = ref<string | null>(localStorage.tenantCode ?? null);
  const programCode = ref<string | null>(localStorage.programCode ?? null);
  const currentEpisodeId = ref<string | null>(
    localStorage.currentEpisodeId ?? null
  );
  const userId = ref<string | null>(localStorage.userId ?? null);
  const login = ref<string | null>(localStorage.login ?? null);
  const name = ref<string | null>(localStorage.name ?? null);
  const measurementStartDate = ref<string | null>(
    localStorage.measurementStartDate ?? null
  );
  const measurementEndDate = ref<string | null>(
    localStorage.measurementEndDate ?? null
  );
  const eulaAccepted = ref<boolean>(
    JSON.parse(localStorage.eulaAccepted || "false")
  );
  const authErrorMessage = ref<string>("");
  const cableConnected = ref<boolean>(false);

  const sessionWarning = ref<boolean>(false);
  let sessionTimeout: ReturnType<typeof setTimeout> | null = null;

  const patientName = computed(() => name.value);
  const showSessionWarning = computed(() => sessionWarning.value);

  // Reactive headers — updates automatically when token/login/programCode change
  const headers = computed<APIHeaders>(() => ({
    "Content-Type": "application/json",
    "X-USER-LOGIN": login.value,
    "X-USER-MONITORING-PROGRAM-CODE": programCode.value,
    "X-USER-TOKEN": accessToken.value,
    "Accept-Language": language,
  }));

  function handleLogin() {
    accessToken.value = localStorage.accessToken ?? null;
    tenantCode.value = localStorage.tenantCode ?? null;
    programCode.value = localStorage.programCode ?? null;
    userId.value = localStorage.userId ?? null;
    login.value = localStorage.login ?? null;
    name.value = localStorage.name ?? null;
    measurementStartDate.value = localStorage.measurementStartDate ?? null;
    measurementEndDate.value = localStorage.measurementEndDate ?? null;
    authErrorMessage.value = "";

    Sentry.setUser({
      id: userId.value,
      username: name.value,
      email: login.value,
    });
    Sentry.setTag("tenant", tenantCode.value);
    Sentry.setTag("program", programCode.value);
    Sentry.setContext("tenant", {
      tenant_code: tenantCode.value,
      program_code: programCode.value,
    });
  }

  function logout() {
    localStorage.clear();
    accessToken.value = null;
    tenantCode.value = null;
    programCode.value = null;
    currentEpisodeId.value = null;
    userId.value = null;
    login.value = null;
    name.value = null;
    measurementStartDate.value = null;
    measurementEndDate.value = null;
    eulaAccepted.value = false;
    sessionWarning.value = false;
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
      sessionTimeout = null;
    }
  }

  function setAuthErrorMessage(message: string) {
    authErrorMessage.value = message;
  }

  function setCurrentEpisodeInfo() {
    currentEpisodeId.value = localStorage.currentEpisodeId ?? null;
    measurementStartDate.value = localStorage.measurementStartDate ?? null;
    measurementEndDate.value = localStorage.measurementEndDate ?? null;
  }

  function setEulaAccepted(value: boolean) {
    localStorage.setItem("eulaAccepted", String(value));
    eulaAccepted.value = value;
  }

  function refreshSessionTimer() {
    sessionWarning.value = false;
    if (sessionTimeout) clearTimeout(sessionTimeout);
    sessionTimeout = setTimeout(() => {
      sessionWarning.value = true;
    }, SESSION_TIMEOUT);
  }

  function setCableConnected(connected: boolean) {
    cableConnected.value = connected;
  }

  return {
    accessToken,
    tenantCode,
    programCode,
    currentEpisodeId,
    userId,
    login,
    name,
    measurementStartDate,
    measurementEndDate,
    eulaAccepted,
    authErrorMessage,
    cableConnected,
    patientName,
    showSessionWarning,
    headers,
    handleLogin,
    logout,
    setAuthErrorMessage,
    setCurrentEpisodeInfo,
    setEulaAccepted,
    refreshSessionTimer,
    setCableConnected,
  };
});
